import React, { useState } from 'react';
import { Wrench, Loader2, CheckCircle2 } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

export const RepairImagesCard = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const run = async (dryRun) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/admin/repair-image-urls', { dry_run: dryRun });
      setResult(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const count = result?.changes.length || 0;

  return (
    <div className="bg-white border border-gray-200 p-8 mt-6" data-testid="repair-images-card">
      <p className="text-xs font-light tracking-[0.2em] uppercase text-gray-500">Maintenance</p>
      <h2 className="text-xl font-light mt-4 mb-2">Réparer les adresses d'images</h2>
      <p className="text-sm font-light text-gray-500 mb-6">
        Recherche les images dont l'adresse est cassée dans les projets, actualités et la philosophie, puis les corrige en une fois.
      </p>

      <div className="flex flex-wrap gap-4">
        <button type="button" onClick={() => run(true)} disabled={loading} data-testid="repair-images-scan-btn"
          className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 font-light text-sm hover:border-[#d94e47] transition-colors disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          Vérifier les images
        </button>
        {result && !result.applied && count > 0 && (
          <button type="button" onClick={() => run(false)} disabled={loading} data-testid="repair-images-apply-btn"
            className="px-6 py-3 bg-[#d94e47] text-white font-light text-sm hover:bg-opacity-90 transition-colors disabled:opacity-50">
            Corriger {count} adresse{count > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {error && <p className="text-[#d94e47] font-light text-sm mt-4" data-testid="repair-images-error">{error}</p>}
      {result && <RepairResult result={result} />}
    </div>
  );
};

const RepairResult = ({ result }) => {
  const count = result.changes.length;
  if (count === 0) {
    return (
      <p className="flex items-center gap-2 text-sm font-light text-green-700 mt-6" data-testid="repair-images-ok">
        <CheckCircle2 className="w-4 h-4" /> {result.scanned} images vérifiées — aucune adresse cassée.
      </p>
    );
  }
  return (
    <div className="mt-6" data-testid="repair-images-result">
      <p className="text-sm font-light mb-3" data-testid="repair-images-summary">
        {result.applied
          ? `${count} adresse${count > 1 ? 's' : ''} corrigée${count > 1 ? 's' : ''} sur ${result.scanned} images.`
          : `${count} adresse${count > 1 ? 's' : ''} cassée${count > 1 ? 's' : ''} trouvée${count > 1 ? 's' : ''} sur ${result.scanned} images.`}
      </p>
      <ul className="max-h-64 overflow-auto border-t border-gray-100 text-xs font-light">
        {result.changes.map((c, i) => (
          <li key={i} className="py-2 border-b border-gray-100" data-testid={`repair-images-change-${i}`}>
            <span className="text-gray-500">{c.collection} · {c.item}</span>
            <div className="break-all"><span className="text-[#d94e47] line-through">{c.before}</span> → <span>{c.after}</span></div>
          </li>
        ))}
      </ul>
    </div>
  );
};
